import { action } from '@ember/object';
import Component from '@glimmer/component';

import type { DSSetup } from 'webapp-types';
import { DataSelectType, GranulationTypeId } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type { DataSelectProps } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type VisualizationBarChart from 'webapp/models-custom/visualization/bar-chart';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';

interface Args {
	blockM: Block;
	dataSelect: DataSelect;
}

export default class BlockSettingsMetricShowNumberComponent extends Component<Args> {
	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization(): VisualizationLineChart | VisualizationBarChart {
		return this.block.blockSetup.visualization as
			VisualizationLineChart | VisualizationBarChart;
	}

	get numberDataSelect(): DataSelect | null {
		return this.visualization.getNumberForChartDs(this.args.dataSelect);
	}

	get selected(): boolean {
		return !!this.numberDataSelect;
	}

	@action
	changeSelected(selected: boolean): void {
		let trackValue: boolean | null = null;
		const numberDataSelect = this.numberDataSelect;
		if (numberDataSelect) {
			if (!selected) {
				this.blockSetup.deleteDataSelect(numberDataSelect);
				trackValue = false;
			}
		} else {
			if (selected) {
				const model = this.args.dataSelect;
				const dateRange = model.limitations.dateRange?.createCopy();

				if (!dateRange) {
					return;
				}

				dateRange.updateFromPredefinedGranulationId(GranulationTypeId.TOTAL);

				// copying compares from the main ds but remove ids
				const compares = copyObject(model.compares);
				compares.forEach((c: DSSetup.Compare) => {
					delete c.id;
				});

				const props: DataSelectProps = {
					type: DataSelectType.MAIN,
					updateFrom: model.numericId,
					dateRange: dateRange.formula,
					compares,
				};
				const metricSetupData = model.metricSetupData;
				if (metricSetupData) {
					props.metricSetupKey = metricSetupData.key;
					props.metricSetupSpaceAccessId = metricSetupData.spaceAccessId;
					props.metricSetupDimensions = metricSetupData.dimensions;
					props.metricSetupFilters = metricSetupData.filters;
				}
				this.blockSetup.createDataSelect(props);
				trackValue = true;
			}
		}

		if (typeof trackValue === 'boolean') {
			this.blockSetup.trackBlockPropertyMetricSettingsChangeEvent(this.args.dataSelect, {
				total_number: trackValue,
			});
		}
	}
	<template>
		<div class="row row--toggle">
			<FormCheckboxSwitchComponent
				@name="showNumber"
				@selected={{this.selected}}
				@onChange={{this.changeSelected}}
			/>
			<label>Show total number above</label>
		</div>
	</template>
}
